import React, { useCallback, useEffect, useState } from "react";
import { FileDown, History, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import { toast } from "sonner";
import { downloadExport } from "@/lib/exportHelpers";

export default function Exports() {
  const [exports, setExports] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: hist }, { data: p }] = await Promise.all([
        api.get("/exports"),
        api.get("/export/preview"),
      ]);
      setExports(hist);
      setPreview(p);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (mode) => {
    setBusy(true);
    const { ok } = await downloadExport(mode);
    if (ok) load();
    setBusy(false);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Exports"
        subtitle="Download approved questions in batches — only new ones by default"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card
          accent
          testid="exports-card-new"
          label="Not yet exported"
          count={preview?.new ?? "…"}
          help="Default — the safe, additive export"
          actionLabel="Export New"
          disabled={busy || preview?.new === 0}
          onClick={() => download("new")}
        />
        <Card
          testid="exports-card-all"
          label="All approved"
          count={preview?.all ?? "…"}
          help="Everything in the approved pool"
          actionLabel="Export All"
          disabled={busy || preview?.all === 0}
          onClick={() => download("all")}
        />
        <Card
          testid="exports-card-exported"
          label="Previously exported"
          count={preview?.exported ?? "…"}
          help="Re-download what's already gone out"
          actionLabel="Re-download"
          disabled={busy || preview?.exported === 0}
          onClick={() => download("exported")}
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 brand-shadow overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Export History
          </h3>
          <span className="ml-2 text-xs font-mono text-slate-400">
            {exports.length} export{exports.length === 1 ? "" : "s"}
          </span>
        </div>
        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-slate-400">Loading…</div>
        ) : exports.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <FileDown className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-500">
              No exports yet. Use the cards above to create your first export.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="exports-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Mode</th>
                <th className="px-5 py-3">Count</th>
                <th className="px-5 py-3">By</th>
                <th className="px-5 py-3">Export ID</th>
              </tr>
            </thead>
            <tbody>
              {exports.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`export-row-${e.export_id}`}
                  className="border-b border-slate-50 hover:bg-slate-50/60"
                >
                  <td className="px-5 py-3 text-slate-700 text-xs">
                    {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider ${
                        e.mode === "new"
                          ? "bg-orange-50 text-orange-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {e.mode}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono text-slate-700 text-xs">{e.count}</td>
                  <td className="px-5 py-3 text-slate-700 text-xs">
                    {e.created_by_name || "—"}
                  </td>
                  <td className="px-5 py-3 font-mono text-[10px] text-slate-500 truncate max-w-[220px]">
                    {e.export_id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PageContainer>
  );
}

function Card({ label, count, help, actionLabel, onClick, disabled, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-5 brand-shadow flex flex-col ${
        accent ? "bg-orange-50/40 border-orange-200" : "bg-white border-slate-100"
      }`}
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </div>
      <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
        {count}
      </div>
      <div className="text-xs text-slate-500 mt-1 mb-4">{help}</div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-testid={`${testid}-btn`}
        className={`mt-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${
          accent
            ? "bg-orange-500 hover:bg-orange-600 text-white"
            : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
        }`}
      >
        <Download className="w-4 h-4" />
        {actionLabel}
      </button>
    </div>
  );
}
